import { z } from 'zod';
import { SettingKey } from '../constants';
import { uuidSchema } from './common';
/**
 * Site settings: a typed key/value store where each key has its own schema.
 *
 * This is where the marketing claims from the reference design live. The design
 * shipped a client logo wall (Netflix, Meta, Google, Microsoft, Amazon), headline
 * statistics (500+ placements, 98% retention, 14 days to hire) and a San
 * Francisco head office — none of which describe this business. Rather than
 * hard-code invented proof into the bundle, those blocks read from here and are
 * seeded empty and disabled. The client turns each on once it has something true
 * to put in it. See docs/CONTENT-BRIEF.md.
 */
export const brandSettingSchema = z.object({
    name: z.string().trim().min(1).max(80).default('The BlackBox Talent'),
    /**
     * "Finding your purpose" is the client's existing tagline, kept because they
     * explained the reasoning behind it on the call while remaining open to
     * alternatives. Editable here so a change needs no deploy.
     */
    tagline: z.string().trim().max(160).default('Finding your purpose'),
    /**
     * One-line positioning statement. Note what it does not say: the previous
     * site's "automate 50% of your hiring journey" was removed because the
     * company does not sell a product.
     */
    positioning: z
        .string()
        .trim()
        .max(300)
        .default('A recruitment boutique matching exact skills, education and experience.'),
    logoMediaId: uuidSchema.nullish(),
    logoDarkMediaId: uuidSchema.nullish(),
    faviconMediaId: uuidSchema.nullish(),
    primaryCtaLabel: z.string().trim().max(40).default('Hire talent'),
    primaryCtaHref: z.string().trim().max(200).default('/contact'),
});
const officeSchema = z.object({
    label: z.string().trim().min(1).max(80),
    addressLines: z.array(z.string().trim().max(160)).max(5).default([]),
    city: z.string().trim().max(80).default(''),
    country: z.string().trim().max(80).default(''),
    isPrimary: z.boolean().default(false),
});
export const contactSettingSchema = z.object({
    email: z.string().trim().max(254).default(''),
    phone: z.string().trim().max(40).default(''),
    /**
     * Seeded with Dubai (primary) and India, per the client's description of the
     * business. The design's San Francisco address was fictional.
     */
    offices: z.array(officeSchema).max(10).default([]),
    /** Shown near the form, e.g. "We reply within one business day." */
    responsePromise: z.string().trim().max(160).default(''),
});
export const socialSettingSchema = z.object({
    linkedin: z.string().trim().max(300).default(''),
    x: z.string().trim().max(300).default(''),
    instagram: z.string().trim().max(300).default(''),
    youtube: z.string().trim().max(300).default(''),
});
export const statsSettingSchema = z.object({
    /**
     * Off until the client supplies figures it can stand behind. The homepage
     * simply omits the band while this is false, with no layout gap.
     */
    enabled: z.boolean().default(false),
    items: z
        .array(z.object({
        value: z.string().trim().min(1).max(20),
        label: z.string().trim().min(1).max(40),
    }))
        .max(6)
        .default([]),
});
export const clientLogosSettingSchema = z.object({
    /**
     * Off by default. Displaying a company's mark implies a commercial
     * relationship, so each entry needs the client's confirmation before it goes
     * live — hence `permissionConfirmed`, which the admin UI requires before it
     * will let the section be enabled.
     */
    enabled: z.boolean().default(false),
    heading: z.string().trim().max(80).default('Trusted by'),
    logos: z
        .array(z.object({
        name: z.string().trim().min(1).max(80),
        mediaId: uuidSchema.nullish(),
        /**
         * A logo shipped as a static file in `apps/web/public` rather than uploaded
         * through the media library, e.g. `/clients_logos/BT.webp`.
         *
         * Constrained to a root-relative path: these render into an `img src`, and
         * allowing an absolute URL here would let a settings write point the strip at
         * any third-party origin. `mediaId` still wins where both are set.
         */
        src: z
            .string()
            .trim()
            .max(300)
            .regex(/^\/[\w\-./]*$/, 'Must be a root-relative path')
            .nullish(),
        href: z.string().trim().max(300).nullish(),
        permissionConfirmed: z.boolean().default(false),
    }))
        .max(24)
        .default([]),
});
export const seoDefaultsSettingSchema = z.object({
    /** Appended to page titles, e.g. "RPO explained | The BlackBox Talent". */
    titleSuffix: z.string().trim().max(60).default('The BlackBox Talent'),
    defaultMetaDescription: z.string().trim().max(180).default(''),
    defaultOgImageId: uuidSchema.nullish(),
    twitterHandle: z.string().trim().max(40).default(''),
    /** Master switch for `noindex` across the whole site — use on staging. */
    noIndexEntireSite: z.boolean().default(false),
    /** Injected into JSON-LD Organization markup. */
    organizationLegalName: z.string().trim().max(160).default(''),
});
export const teamSettingSchema = z.object({
    enabled: z.boolean().default(true),
    heading: z.string().trim().max(80).default('Our team'),
    members: z
        .array(z.object({
        name: z.string().trim().min(1).max(120),
        title: z.string().trim().max(120).default(''),
        bio: z.string().trim().max(600).default(''),
        photoMediaId: uuidSchema.nullish(),
        email: z.string().trim().max(254).default(''),
        linkedin: z.string().trim().max(300).default(''),
        sortOrder: z.number().int().min(0).default(0),
    }))
        .max(24)
        .default([]),
});
/**
 * The two image-led blocks on the homepage: the hero, and the "Why we exist"
 * split.
 *
 * Both slots are nullable and both blocks fall back to the abstract composition
 * they shipped with, so an empty setting is a complete page rather than two holes.
 * That fallback is the reason this is safe to ship enabled: there is no state in
 * which the homepage depends on an image being present.
 *
 * Alt text lives beside each id rather than being taken from the asset's own
 * `altText`. The same picture can be decorative in one place and meaningful in
 * another, and the block using it is what knows which.
 */
export const homepageMediaSettingSchema = z.object({
    heroImageId: uuidSchema.nullish(),
    heroImageAlt: z.string().trim().max(300).default(''),
    aboutImageId: uuidSchema.nullish(),
    aboutImageAlt: z.string().trim().max(300).default(''),
});
/**
 * Toggles for homepage sections that exist in code but may not always be
 * appropriate to show. Everything the reference design contained is built; this
 * is how the client decides what is actually live.
 */
export const featureFlagsSettingSchema = z.object({
    showCapabilityGrid: z.boolean().default(true),
    showProcessSection: z.boolean().default(true),
    showInsightsSection: z.boolean().default(true),
    showBlogOnHomepage: z.boolean().default(true),
    /** Phase 2. The design's "Find a job" / "Careers" surfaces stay dark. */
    showCareers: z.boolean().default(false),
    /** Removed with the product pivot; kept as an explicit, documented "off". */
    showRoiCalculator: z.boolean().default(false),
    showScheduleDemo: z.boolean().default(false),
});
/**
 * Maps each key to its schema. The settings service validates writes against
 * this and rejects unknown keys, so a typo cannot create a phantom setting that
 * silently never renders.
 */
export const SETTING_SCHEMAS = {
    [SettingKey.BRAND]: brandSettingSchema,
    [SettingKey.CONTACT]: contactSettingSchema,
    [SettingKey.SOCIAL]: socialSettingSchema,
    [SettingKey.STATS]: statsSettingSchema,
    [SettingKey.CLIENT_LOGOS]: clientLogosSettingSchema,
    [SettingKey.SEO_DEFAULTS]: seoDefaultsSettingSchema,
    [SettingKey.TEAM]: teamSettingSchema,
    [SettingKey.HOMEPAGE_MEDIA]: homepageMediaSettingSchema,
    [SettingKey.FEATURE_FLAGS]: featureFlagsSettingSchema,
};
/**
 * Every key's defaults, derived by parsing `{}` against each schema.
 *
 * Deriving rather than hand-writing a second literal guarantees the defaults and
 * the schema can never disagree, and means a missing row behaves exactly like an
 * unedited one.
 */
export function defaultSettings() {
    return {
        [SettingKey.BRAND]: brandSettingSchema.parse({}),
        [SettingKey.CONTACT]: contactSettingSchema.parse({}),
        [SettingKey.SOCIAL]: socialSettingSchema.parse({}),
        [SettingKey.STATS]: statsSettingSchema.parse({}),
        [SettingKey.CLIENT_LOGOS]: clientLogosSettingSchema.parse({}),
        [SettingKey.SEO_DEFAULTS]: seoDefaultsSettingSchema.parse({}),
        [SettingKey.TEAM]: teamSettingSchema.parse({}),
        [SettingKey.HOMEPAGE_MEDIA]: homepageMediaSettingSchema.parse({}),
        [SettingKey.FEATURE_FLAGS]: featureFlagsSettingSchema.parse({}),
    };
}
//# sourceMappingURL=settings.js.map